/**
 * TeamBonusCallout
 *
 * Replaces the original purple "Invite Team Members" button in the dashboard
 * header for admin users. Renders one of four visual states based on the
 * `/api/stripe/team-bonus-status/{church_id}` endpoint:
 *
 *   - not_started   → amber→orange gradient shimmer pill,
 *                     "Invite your team within {N} days → Get a free month"
 *                     with a trailing ChevronRight as click affordance.
 *   - in_progress   → same gradient pill,
 *                     "{N} more team member(s) in {D} day(s) → Get a free month"
 *                     (both numerals are singular-aware).
 *   - granted       → emerald→green gradient celebration pill (auto-hides
 *                     24 hours after grant via the backend state machine).
 *                     Fires confetti once per device using a localStorage flag.
 *   - hidden        → renders nothing.
 *
 * The backend is the single source of truth for the state machine — this
 * component is a pure renderer with no business logic.
 *
 * Visual design notes (Feb 2026 refresh):
 *   - Gradient fills (vs flat) match top-tier reward-callout pattern
 *     (Stripe, Linear, Notion's trial-extension callouts).
 *   - Subtle ring (12% opacity) + shadow-sm gives the pill depth against
 *     the white dashboard header without competing with the indigo logo.
 *   - Shimmer animation (`invite-shimmer-btn::after` in index.css) is
 *     preserved — the white gradient overlay reads cleanly against the
 *     amber→orange backdrop.
 */
import React, { useEffect, useState } from 'react';
import { UserPlus, Sparkles, Gift, ChevronRight } from 'lucide-react';
import { triggerConfetti } from '../onboarding/utils/confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// localStorage key for "have I already celebrated this church's bonus?"
const seenKey = (churchId) => `wos:team-bonus-celebrated:${churchId}`;

export function TeamBonusCallout({ user, onOpenSettings }) {
  const [status, setStatus] = useState(null);

  // Fetch on mount + when window regains focus (catches the case where an
  // admin invites someone in another tab, they accept, then admin returns
  // to the dashboard — callout updates without a page refresh).
  useEffect(() => {
    if (!user?.church_id) return;

    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stripe/team-bonus-status/${user.church_id}`);
        if (!res.ok) {
          if (!cancelled) setStatus({ state: 'hidden' });
          return;
        }
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus({ state: 'hidden' });
      }
    };

    fetchStatus();
    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.church_id]);

  // Fire confetti exactly once per device per church when we observe the
  // 'granted' state for the first time. Uses localStorage so refreshing
  // doesn't re-trigger confetti, but a different device (or cleared
  // storage) gets to see it.
  useEffect(() => {
    if (status?.state !== 'granted' || !user?.church_id) return;
    try {
      if (localStorage.getItem(seenKey(user.church_id))) return;
      localStorage.setItem(seenKey(user.church_id), '1');
      triggerConfetti();
    } catch {
      // localStorage unavailable (Safari private mode) — silently skip
    }
  }, [status?.state, user?.church_id]);

  // Initial mount: render nothing rather than a layout-shifting placeholder
  if (!status || status.state === 'hidden') return null;

  // GRANTED — celebration pill (emerald → green gradient)
  if (status.state === 'granted') {
    const dateLabel = formatTrialEndDate(status.new_trial_end);
    return (
      <button
        type="button"
        data-testid="team-bonus-callout-granted"
        onClick={() => onOpenSettings?.('billing')}
        data-ui="bonus-pill"
        className="ml-2 px-4 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transition-all ring-1 ring-emerald-300/40 shadow-sm whitespace-nowrap inline-flex items-center gap-2"
        title="View your billing details"
      >
        <Sparkles className="w-3.5 h-3.5 drop-shadow-sm" aria-hidden="true" />
        <span>
          Your team's all set! Free month unlocked
          {dateLabel ? ` · Trial ends ${dateLabel}` : ''}
        </span>
      </button>
    );
  }

  // NOT_STARTED / IN_PROGRESS — amber → orange gradient shimmer pill
  const isNotStarted = status.state === 'not_started';
  const remaining = Math.max(0, Number(status.remaining_members) || 0);
  const daysLeft = Math.max(1, Number(status.days_remaining_in_window) || 1);
  const bonusDays = Number(status.bonus_days) || 30;

  // DRY: both states end the same way. Build label fragments once.
  const dayLabel = `${daysLeft} ${plural(daysLeft, 'day', 'days')}`;
  const rewardLabel = bonusDays === 30 ? 'month' : `${bonusDays} days`;

  // not_started:  "Invite your team within 5 days → Get a free month"
  // in_progress:  "4 more team members in 5 days → Get a free month" / "1 more team member in 1 day → Get a free month"
  const headline = isNotStarted
    ? `Invite your team within ${dayLabel} → Get a free ${rewardLabel}`
    : `${remaining} more team ${plural(remaining, 'member', 'members')} in ${dayLabel} → Get a free ${rewardLabel}`;

  const testid = isNotStarted ? 'team-bonus-callout-not-started' : 'team-bonus-callout-in-progress';

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onOpenSettings?.('team')}
      data-ui="bonus-pill"
      className="invite-shimmer-btn ml-2 px-4 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 hover:from-amber-500 hover:to-orange-600 transition-all ring-1 ring-amber-300/40 shadow-sm relative overflow-hidden whitespace-nowrap inline-flex items-center gap-2"
      title={isNotStarted ? 'Click to invite your first team member' : 'Click to invite more team members'}
    >
      {isNotStarted ? (
        <Gift className="w-3.5 h-3.5 relative z-10 drop-shadow-sm" aria-hidden="true" />
      ) : (
        <UserPlus className="w-3.5 h-3.5 relative z-10 drop-shadow-sm" aria-hidden="true" />
      )}
      <span className="relative z-10">{headline}</span>
      <ChevronRight className="w-3.5 h-3.5 relative z-10 -mr-1 opacity-90" aria-hidden="true" />
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers (kept local to keep the component self-contained)
// ────────────────────────────────────────────────────────────────────────────

function plural(n, singular, pluralForm) {
  return n === 1 ? singular : pluralForm;
}

function formatTrialEndDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default TeamBonusCallout;
